import type { BreadcrumbProps } from '@blueprintjs/core'
import { observer } from 'mobx-react-lite'
import React, { useMemo } from 'react'
import { Filters, PerformanceFilters, TableName } from '../../config'
import { useKdpFilters } from '../../hooks/useKdpFilters'
import { useUser } from '../../hooks/useUser'
import type { TableHooks } from '../../packages/blueprint-components/src/useTableHooks'
import type { AdGroup } from '../../types/ad_group'
import { addExtraCols, spliceAfter } from '../campaigns/CampaignsTable'
import { Layout } from '../Layout'
import {
    Columns,
    defaultPerfColumns,
    type ExpandedPerformanceMetrics,
} from '../table2/ad_grid_columns'
import { type AgGridColumnDef, AgGridTable } from '../table2/AgGridTable'
import { useBidLimits } from '../bid_limits/useBidLimits'
import type { SavedFiltersStore } from '../table2/Filter3'
import type { TableStore } from '../table2/table_store'
import { OverviewCharts } from '../../pages/dashboard-overview-charts'
import { useAdManagerCustomFields } from '../../features/custom-fields/ad-manager'

export const AdGroupType = {
    Keyword: 1,
    Target: 2,
    Auto: 3,
    Empty: 4,
}

const defaultFilters = [
    Filters.PortfolioId,
    Filters.CampaignId,
    Filters.StateWithSplit,
    Filters.DeliveryStatus,
    Filters.DeliveryReason,
    Filters.TargetingType,
    Filters.DefaultBid,
    Filters.TargetACOS,
    ...PerformanceFilters,
]

const mutableFields: string[] = ['defaultBid', 'state', 'ad_group_id']

export interface TableProps<T> {
    nested?: boolean
    tableHooks: TableHooks<T>
    store: TableStore<T>
    savedFiltersStore: SavedFiltersStore
    showCharts?: boolean
}

export const AdGroupsTable = observer(
    ({
        nested,
        store,
        savedFiltersStore,
        tableHooks,
        showCharts = true,
    }: TableProps<AdGroup & ExpandedPerformanceMetrics>): JSX.Element => {
        const useUserValues = useUser()
        const filters = useKdpFilters(defaultFilters)
        useBidLimits(store, 2, 'ad_group_id')
        const customFields = useAdManagerCustomFields<AdGroup & ExpandedPerformanceMetrics>({
            profileId: useUserValues.profile === null ? null : String(useUserValues.profile),
            entityType: 'ad_group',
            entityIdKey: 'ad_group_id',
            store,
            reload: tableHooks.reload,
        })

        const breadcrumbs: BreadcrumbProps[] = [{ text: 'Manage' }]

        const cols3 = useMemo(() => {
            return [...newColumns(useUserValues.is_kdp), ...customFields.columns]
        }, [customFields.columns, useUserValues.is_kdp])

        return (
            <Layout
                breadcrumbs={breadcrumbs}
                primary={TableName.AdGroup}
                nested={nested}
                params={tableHooks.params}
            >
                {showCharts && (
                    <OverviewCharts
                        params={tableHooks.params}
                        className="mb-4"
                        chartType="ad_groups"
                    />
                )}
                <AgGridTable
                    name={TableName.AdGroup}
                    columns={cols3}
                    tableHooks={tableHooks}
                    store={store}
                    showHeader={false}
                    savedFiltersStore={savedFiltersStore}
                    filters={filters}
                    selectKey="ad_group_id"
                    stableRowId
                    search
                    searchPlaceholder="Search by ad group or campaign"
                    columnSelection
                    mutableFields={mutableFields}
                    bulkActions
                    otherActionsLeft={customFields.bulkAction}
                    nested={nested}
                />
            </Layout>
        )
    },
)

const newColumns = <T,>(isKdp: boolean): AgGridColumnDef<T>[] => {
    const cols: AgGridColumnDef<T>[] = [
        Columns.Select,
        Columns.AdGroupName,
        Columns.AdGroupBreadcrumbs,
        Columns.ProductAdsCount,
        Columns.DefaultBid,
        Columns.EffectiveBid,
        Columns.BidStrategy,
        Columns.PlacementBidAdjustments,
        Columns.LastDefaultBidChange,
        Columns.BidLimits,
        ...defaultPerfColumns,
    ]

    spliceAfter('name', cols, Columns.DeliveryStatus)
    addExtraCols(cols, isKdp)

    return cols
}
